import { Logger } from '~~/helpers/logger';
import { useReviewApi } from '~/utils/ct/composables/api/useReviewApi';
import type {
  ApiParams,
  CustomFieldsType,
  GetReviewParams,
  RawCustomField,
  Review,
  UseReviewData,
} from '~~/types/commercetools';
import { errorToString } from '~~/helpers/error/errorToString';
import { filterLocaleMapValue } from '~~/helpers/customFields';

type ReviewCustomFields = CustomFieldsType & {
  titleTranslations?: Array<RawCustomField> | null;
  textTranslations?: Array<RawCustomField> | null;
  answerTranslations?: Array<RawCustomField> | null;
};

type TranslationField = Exclude<keyof ReviewCustomFields, keyof CustomFieldsType>;
const TRANSLATION_FIELDS: Array<TranslationField> = ['titleTranslations', 'textTranslations', 'answerTranslations'];

function collapseLocaleMap(
  fields: Array<RawCustomField> | null | undefined,
  keepLocales: Set<string>,
): Array<RawCustomField> | null | undefined {

  if (!fields?.length) {

    return fields;

  }

  return fields.map(field => {

    const filtered = filterLocaleMapValue(field.value, keepLocales);

    return filtered !== field.value ? { ...field, value: filtered } : field;

  });

}

function collapseReviewTranslations(review: Review, currentLocale: string): Review {

  if (!review.custom) {

    return review;

  }

  const customFields = review.custom as ReviewCustomFields;
  const reviewOriginalLocale = review.locale;
  const localesToKeep = new Set([currentLocale, ...(reviewOriginalLocale ? [reviewOriginalLocale] : [])]);

  const collapsedTranslations: Partial<Record<TranslationField, Array<RawCustomField> | null | undefined>> = {};

  for (const translationField of TRANSLATION_FIELDS) {

    const collapsed = collapseLocaleMap(customFields[translationField], localesToKeep);

    if (collapsed !== customFields[translationField]) {

      collapsedTranslations[translationField] = collapsed;

    }

  }

  const hasChanges = Object.keys(collapsedTranslations).length > 0;

  if (!hasChanges) {

    return review;

  }

  return { ...review, custom: { ...customFields, ...collapsedTranslations } };

}

export function useReview(id: string) {

  const { search: apiSearch } = useReviewApi();
  const { languageAndCountry } = useLocalization();
  const reviews = useState<UseReviewData | undefined>(`useReviews-reviews-${id}`, () => undefined);
  const loading = useState(`useReviews-loading-${id}`, () => false);

  const error = useState<{
    search: null | string,
  }>(`useReviews-error-${id}`, () => ({
    search: null,
  }));

  async function search(searchParams: ApiParams<GetReviewParams>) {

    Logger.debug(`useReview/${id}/search`, searchParams);
    try {

      loading.value = true;
      const data = await apiSearch(searchParams);
      const currentLocale = languageAndCountry.value;

      if (currentLocale && data?.results?.length) {

        const collapsedResults = data.results.map(review => collapseReviewTranslations(review, currentLocale));

        reviews.value = { ...data, results: collapsedResults };

      } else {

        reviews.value = data;
      
      }
      error.value.search = null;
    
    } catch (err: unknown) {

      error.value.search = errorToString(err);
      Logger.error(`useReview/${id}/search`, err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  return {
    search,
    reviews: computed(() => reviews.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };

}
